/**
 * Every number, date and enum label the panel shows goes through here.
 *
 * ## Two decisions, applied everywhere (brief §0)
 *
 * **Western digits, Gregorian calendar.** `ar-SA` alone means the Umm al-Qura
 * calendar and Eastern Arabic numerals, which is right for prose and wrong for
 * an operator lining an audit row up against a server log, or reading a spend
 * figure beside a slug. `-u-ca-gregory-nu-latn` keeps the language and fixes
 * both. One choice, made here, so no screen makes the other.
 *
 * **Riyadh time.** The server bills by the month "from the database clock in
 * Riyadh" (`TenantUsage.since`), so a date shown in the operator's own zone
 * could put a 23:30 spend in the wrong month. Pinned, and stated in the UI
 * where it matters.
 *
 * Every function takes `null | undefined` where the API can send null, and
 * renders `—` for it — a blank cell reads as "not loaded", a dash as "none".
 */

import { hasMessage, t, type MessageKey } from './i18n';
import type {
  AttentionKind,
  DispatchStatus,
  EmailTemplateCategory,
  JoinRequestStatus,
  MemberStatus,
  ModelCostClass,
  ModelTier,
  OperatorRole,
  PlatformPermission,
  TenantRole,
} from './platform';

export const LOCALE = 'ar-SA-u-ca-gregory-nu-latn';
export const TIME_ZONE = 'Asia/Riyadh';

const DASH = '—';

/* ------------------------------------------------------------------ */
/* Numbers, money, percentages, bytes                                   */
/* ------------------------------------------------------------------ */

const integer = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 });
const compact = new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 });

function missing(value: number | null | undefined): value is null | undefined {
  return value === null || value === undefined || !Number.isFinite(value);
}

/** `1,234` — counts. Rounds; a count with a fraction is a bug upstream, not a thing to show. */
export function formatCount(value: number | null | undefined): string {
  return missing(value) ? DASH : integer.format(value);
}

/** `1,234.57` — up to two decimals, none when whole. */
export function formatNumber(value: number | null | undefined): string {
  return missing(value) ? DASH : decimal.format(value);
}

/** `1.2 مليون` — KPI tiles where the exact figure is one hover away. */
export function formatCompact(value: number | null | undefined): string {
  return missing(value) ? DASH : compact.format(value);
}

const sar = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'SAR' });
const sarWhole = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'SAR', maximumFractionDigits: 0 });
const sarCompact = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'SAR',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Saudi riyals. The API's money fields are SAR numbers (`spendSar`, `spend.used`).
 *
 * `whole` drops the halalas for tiles and meters; `compact` for charts' axes.
 * A cap of null is *no limit* — use `formatCap`, which says so, rather than
 * this, which would say "—" and read as "unknown".
 */
export function formatMoney(value: number | null | undefined, style: 'exact' | 'whole' | 'compact' = 'exact'): string {
  if (missing(value)) return DASH;

  return (style === 'whole' ? sarWhole : style === 'compact' ? sarCompact : sar).format(value);
}

/** A spending cap: null is "بلا حد", which is not zero and not unknown. */
export function formatCap(cap: number | null | undefined, style: 'exact' | 'whole' | 'compact' = 'whole'): string {
  if (cap === null) return t('common.unlimited');

  return formatMoney(cap, style);
}

const percent = new Intl.NumberFormat(LOCALE, { style: 'percent', maximumFractionDigits: 1 });

/** A ratio (0.42) as `42٪`. Not clamped — 130% over a cap is the point of showing it. */
export function formatPercent(ratio: number | null | undefined): string {
  return missing(ratio) ? DASH : percent.format(ratio);
}

/**
 * Used against a cap, as a ratio for a meter. Null when there is no cap — a
 * meter with no ceiling has nothing to fill. A cap of zero with anything spent
 * is full, not infinite.
 */
export function usageRatio(used: number, cap: number | null): number | null {
  if (cap === null) return null;
  if (cap <= 0) return used > 0 ? 1 : 0;

  return used / cap;
}

export type UsageLevel = 'ok' | 'near' | 'over';

/** The meter's colour band. 80% is "near" — the same line the brief's SPEND_NEAR_CAP uses by name. */
export function usageLevel(ratio: number | null): UsageLevel {
  if (ratio === null || ratio < 0.8) return 'ok';

  return ratio < 1 ? 'near' : 'over';
}

const BYTE_UNITS: [MessageKey, number][] = [
  ['unit.gigabyte', 1024 ** 3],
  ['unit.megabyte', 1024 ** 2],
  ['unit.kilobyte', 1024],
];

/** Binary multiples (the upload code thinks in MiB), one decimal. */
export function formatBytes(bytes: number | null | undefined): string {
  if (missing(bytes)) return DASH;

  for (const [key, size] of BYTE_UNITS) {
    if (Math.abs(bytes) >= size) return t(key, { value: decimal.format(Math.round((bytes / size) * 10) / 10) });
  }

  return t('unit.byte', { value: integer.format(bytes) });
}

/* ------------------------------------------------------------------ */
/* Dates                                                                */
/* ------------------------------------------------------------------ */

const dateFormat = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long', year: 'numeric', timeZone: TIME_ZONE });
const dateTimeFormat = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: TIME_ZONE,
});
const dayMonthFormat = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', timeZone: 'UTC' });
const monthFormat = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' });
const relativeFormat = new Intl.RelativeTimeFormat('ar-u-nu-latn', { numeric: 'auto' });

function parse(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

/** `7 أكتوبر 2026`. */
export function formatDate(value: string | null | undefined): string {
  const date = parse(value);

  return date === null ? DASH : dateFormat.format(date);
}

/** `7 أكتوبر 2026، 12:05 م` — audit rows, dispatches. Riyadh time. */
export function formatDateTime(value: string | null | undefined): string {
  const date = parse(value);

  return date === null ? DASH : dateTimeFormat.format(date);
}

/** `7 سبتمبر` — a chart axis label for a `YYYY-MM-DD` day (the insights' `weekStart`). Read as a calendar date, not an instant. */
export function formatDay(value: string | null | undefined): string {
  const date = parse(value === null || value === undefined ? value : `${value.slice(0, 10)}T00:00:00Z`);

  return date === null ? DASH : dayMonthFormat.format(date);
}

/** `أكتوبر 2026` — for `insights.month` (`YYYY-MM`). */
export function formatMonth(value: string | null | undefined): string {
  if (value === null || value === undefined || !/^\d{4}-\d{2}$/.test(value)) return DASH;

  return monthFormat.format(new Date(`${value}-01T00:00:00Z`));
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/**
 * `قبل 3 ساعات`, `أمس`. Null is "never" — a last-seen that never happened —
 * and is said in words, not as a dash. Pair it with `formatDateTime` in a
 * `title` so the exact moment is one hover away.
 *
 * `now` is a parameter so the function is testable and so a list can render
 * every row against the same instant.
 */
export function formatRelative(value: string | null | undefined, now: number = Date.now()): string {
  if (value === null || value === undefined) return t('time.never');

  const date = parse(value);

  if (date === null) return DASH;

  const seconds = (date.getTime() - now) / 1000;

  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) return relativeFormat.format(Math.round(seconds / size), unit);
  }

  return t('time.now');
}

/* ------------------------------------------------------------------ */
/* Durations                                                            */
/* ------------------------------------------------------------------ */

const DURATION_UNITS: readonly ['day' | 'hour' | 'minute', number][] = [
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

const durationUnitFormats = new Map<string, Intl.NumberFormat>();

function durationUnit(value: number, name: 'day' | 'hour' | 'minute' | 'second'): string {
  let format = durationUnitFormats.get(name);

  if (format === undefined) {
    format = new Intl.NumberFormat(LOCALE, { style: 'unit', unit: name, unitDisplay: 'long' });
    durationUnitFormats.set(name, format);
  }

  return format.format(value);
}

const durationConjunction = new Intl.ListFormat('ar', { type: 'conjunction', style: 'long' });

/**
 * A length of time in seconds: `3 ساعات و12 دقيقة`, `يومان و4 ساعات`,
 * `45 ثانية` — at most two units, the largest first. ICU carries Arabic's dual
 * and plural forms (ساعتان، 3 ساعات، 11 ساعة), which a template string could
 * not. Null, negative or not a number — nothing is waiting — is a dash.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return DASH;

  const whole = Math.floor(seconds);

  if (whole < 60) return durationUnit(whole, 'second');

  const index = DURATION_UNITS.findIndex(([, size]) => whole >= size);
  const [name, size] = DURATION_UNITS[index] ?? ['minute', 60];
  const major = Math.floor(whole / size);
  const next = DURATION_UNITS[index + 1];
  const minor = next === undefined ? 0 : Math.floor((whole - major * size) / next[1]);

  return next === undefined || minor === 0
    ? durationUnit(major, name)
    : durationConjunction.format([durationUnit(major, name), durationUnit(minor, next[0])]);
}

/* ------------------------------------------------------------------ */
/* Labels and tones                                                     */
/* ------------------------------------------------------------------ */

/*
 * The closed families resolve by concatenation, so the compiler checks the
 * catalogue has every member: add a role to the API and `role.${TenantRole}`
 * stops being a `MessageKey` until it is named in `i18n/vocabulary.ts`.
 */

export const roleLabel = (role: TenantRole): string => t(`role.${role}`);
export const memberStatusLabel = (status: MemberStatus): string => t(`memberStatus.${status}`);
export const tenantStatusLabel = (isActive: boolean): string => t(isActive ? 'tenantStatus.active' : 'tenantStatus.suspended');
export const operatorRoleLabel = (role: OperatorRole): string => t(`operatorRole.${role}`);
export const platformPermissionLabel = (permission: PlatformPermission): string => t(`platformPermission.${permission}`);
export const joinStatusLabel = (status: JoinRequestStatus): string => t(`joinStatus.${status}`);
export const dispatchStatusLabel = (status: DispatchStatus): string => t(`dispatchStatus.${status}`);
export const templateCategoryLabel = (category: EmailTemplateCategory): string => t(`templateCategory.${category}`);
export const attentionLabel = (kind: AttentionKind): string => t(`attention.${kind}`);
export const modelTierLabel = (tier: ModelTier): string => t(`modelTier.${tier}`);
export const modelCostLabel = (cost: ModelCostClass): string => t(`modelCost.${cost}`);

/*
 * The open families fall back to the raw value: a permission code or audit
 * action the server added after this build is still evidence, and a blank
 * would hide it.
 */

/** A tenant permission code (`USER_MANAGE`). */
export function permissionLabel(code: string): string {
  const key = `permission.${code}`;

  return hasMessage(key) ? t(key) : code;
}

/** An audit action, platform (`platform.tenant_created`) or tenant (`user.suspended`). */
export function actionLabel(action: string): string {
  const key = `action.${action}`;

  return hasMessage(key) ? t(key) : action;
}

/**
 * The tone a status chip takes, so status is legible before the label is read.
 * Never the only signal (brief §4): the chip also carries the label.
 */
export type Tone = 'neutral' | 'success' | 'warning' | 'danger';

export const MEMBER_STATUS_TONE: Readonly<Record<MemberStatus, Tone>> = {
  ACTIVE: 'success',
  INVITED: 'warning',
  SUSPENDED: 'danger',
};

export const JOIN_STATUS_TONE: Readonly<Record<JoinRequestStatus, Tone>> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'neutral',
};

export const DISPATCH_STATUS_TONE: Readonly<Record<DispatchStatus, Tone>> = {
  QUEUED: 'neutral',
  SENT: 'success',
  FAILED: 'danger',
};

export const tenantStatusTone = (isActive: boolean): Tone => (isActive ? 'success' : 'danger');

export const USAGE_TONE: Readonly<Record<UsageLevel, Tone>> = { ok: 'success', near: 'warning', over: 'danger' };

/**
 * Two letters for an avatar. Used by the shell's account menu.
 *
 * The definite article is skipped: without that, every surname that starts
 * with ال (القحطاني, العتيبي, الشمري — most of them) has the same initial, ا.
 */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initial = (word: string | undefined) => (word === undefined ? '' : (/^ال(.)/u.exec(word)?.[1] ?? word[0] ?? ''));
  const first = initial(parts[0]) || '؟';
  const second = parts.length > 1 ? initial(parts[parts.length - 1]) : '';

  return `${first}${second}`;
}
